import React, { useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import useTranslation from 'hooks/use-translation';

import { Button, Grid } from 'components/core';
import Footer from 'components/global-footer-rebrand';
import MarketingHeader from 'marketing/components/marketing-header';
import MarketingSpotlight from 'marketing/components/marketing-spotlight';
import MetaTags from 'components/meta-tags';
import Wrapper from 'components/wrapper';
import { DesktopOnly, MobileOnly } from 'shared/components';
import Imgix from 'shared/components/imgix';

import { Header, H2, Label, Meta, Title } from './components';

export default function NewsPage() {
  const { t, Trans } = useTranslation();
  const [visiblePublications, setVisiblePublications] = useState(6);
  const spotlightProps = {
    copy: (
      <Trans i18nKey='news.rossAndZach'>
        Dutchie raised its largest funding to date, $200 million in a Series C, to value the company at $1.7 billion.
        The new round also coincides with their acquisitions of LeafLogix and Greenbits, two of the leading cannabis
        point of sales...
      </Trans>
    ),
    cta: (
      <Button
        variant='primary'
        size='medium'
        onClick={() =>
          window.open(
            `https://news.crunchbase.com/news/cannabis-technology-platform-dutchie-rolls-up-200m-series-c-to-hit-1-7b-valuation/`,
            `_blank`
          )
        }
      >
        {t('about.readMore', 'Read More')}
      </Button>
    ),
    header: (
      <Trans i18nKey='about.theseBrothers'>
        Cannabis Technology Platform Dutchie Rolls Up $200M Series C To Hit $1.7B Valuation
      </Trans>
    ),
    img: (
      <Imgix
        src='https://images.dutchie.com/marketing-v3/dutchie-series-c-founders.jpeg?auto=format&q=90&cs=strip&h=auto'
        htmlAttributes={{ alt: t('about.coFounders', 'dutchie co-founders, Ross and Zach') }}
      />
    ),
    label: t('about.featuredStory', 'Featured Story'),
  };

  const publicationsMap = [
    {
      key: 'cnbc',
      logo: `images/cnbc.png`,
      alt: `CNBC logo`,
      // eslint-disable-next-line max-len
      title: t(
        'news.startupDutchie',
        'Start-up Dutchie to buy cannabis retail software developers Greenbits, LeafLogix'
      ),
      meta: t('news.cnbcDate', { defaultValue: '{{date}} / CNBC', date: 'Mar 16, 2021' }),
      url: `https://www.cnbc.com/2021/03/16/dutchie-raises-200-million-in-funding-acquires-greenbits-leaflogix-.html`,
    },
    {
      key: 'wsj',
      logo: `images/wsj.png`,
      alt: `WSJ logo`,
      title: t('news.techProvider', 'Tech Provider for Cannabis Firms Dutchie Raises $200 Million'),
      meta: t('news.wsjDate', { defaultValue: '{{date}} / WSJ', date: 'Mar 16, 2021' }),
      url: `https://www.wsj.com/articles/tech-provider-for-cannabis-firms-dutchie-raises-200-million-11615887913?tpl=venturecapital`,
    },
    {
      key: 'forbes',
      logo: `http://www.elevationadvertising.com/wp-content/uploads/2018/01/forbes-logo-black-transparent.png`,
      alt: `Forbes logo`,
      title: t('news.dutchieSeeks', 'Dutchie Seeks To Become The ‘Shopify For Cannabis’ With New e-Commerce Offering'),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / Forbes', date: 'Nov 16, 2020' }),
      url: `https://www.forbes.com/sites/javierhasse/2020/11/16/dutchie-launches-customizable-cannabis-e-commerce-platform-cookies-native-roots-sign-up/?sh=4cf5b3fe4bec`,
    },
    {
      key: 'tech-crunch',
      logo: `images/tech-crunch.png`,
      alt: `TechCrunch logo`,
      title: t(
        'news.salesAreUp',
        // eslint-disable-next-line max-len
        'Cannabis dispensaries’ online sales are way up, and Dutchie, which connects them to their customers, is a major beneficiary'
      ),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / TechCrunch', date: 'August 18, 2020' }),
      url: `https://techcrunch.com/2020/08/18/cannabis-dispensaries-online-sales-are-thriving-and-dutchie-which-connects-them-to-their-customers-is-a-major-beneficiary/`,
    },
    {
      key: 'forbes',
      logo: `http://www.elevationadvertising.com/wp-content/uploads/2018/01/forbes-logo-black-transparent.png`,
      alt: `Forbes logo`,
      title: t(
        'news.coffeeToCannabis',
        // eslint-disable-next-line max-len
        'Coffee To Cannabis: Curious What Howard Schultz Is Up To? Here’s The Investment The Starbucks Alum (And Other Big Names) Are Gravitating To'
      ),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / Forbes', date: 'August 18, 2020' }),
      url: `https://www.forbes.com/sites/joanoleck/2020/08/18/coffee-to-cannabis-curious-what-howard-schultz-is-up-to-heres-the-investment-the-starbucks-alum-and-other-big-names-are-gravitating-to/#2d34ae6a6506`,
    },
    {
      key: 'forbes',
      logo: `http://www.elevationadvertising.com/wp-content/uploads/2018/01/forbes-logo-black-transparent.png`,
      alt: `Forbes logo`,
      title: t(
        'news.nowTakingVisa',
        'Online Cannabis Marketplace Dutchie Now Taking Visa, Mastercard Payments In Canada'
      ),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / TechCrunch', date: 'April 15, 2020' }),
      url: `https://www.forbes.com/sites/javierhasse/2020/04/15/dutchie-visa-mastercard`,
    },
    {
      key: 'geek-wire',
      logo: `images/geek-wire.png`,
      alt: `Geekwire logo`,
      title: t(
        'news.kdSnoopDutchie',
        'NBA star Kevin Durant, Snoop Dogg back marijuana startup Dutchie as part of $15M round'
      ),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / GeekWire', date: 'September 9, 2019' }),
      url: `https://www.geekwire.com/2019/nba-star-kevin-durant-snoop-dogg-back-marijuana-startup-dutchie-part-15m-round/`,
    },
    {
      key: 'new-cannabis-ventures',
      logo: `images/new-cannabis-ventures.png`,
      alt: `New Cannabis Ventures logo`,
      title: t('news.dutchieRaisesMillions', 'Cannabis Online Delivery Platform Dutchie Raises $15 Million'),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / New Cannabis Ventures', date: 'September 24, 2019' }),
      url: `https://www.newcannabisventures.com/cannabis-online-delivery-platform-dutchie-raises-15-million/`,
    },
    {
      key: 'tech-crunch',
      logo: `images/tech-crunch.png`,
      alt: `TechCrunch logo`,
      title: t(
        'news.vcSnoopAndDurant',
        'VC firms of Kevin Durant and Snoop Dogg back dutchie, a new cannabis delivery service'
      ),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / TechCrunch', date: 'September 9, 2019' }),
      url: `https://techcrunch.com/2018/09/24/pass-the-dutchie-to-the-left-hand-side/`,
    },
    {
      key: 'pymnts',
      logo: `images/pymnts.png`,
      alt: `Pymnts logo`,
      title: t('news.potCompanyRaises', 'Pot Delivery Company dutchie Raises $3M'),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / Pymnts', date: 'September 24, 2019' }),
      url: `https://www.pymnts.com/news/investment-tracker/2018/pot-delivery-company-dutchie-venture-funding/`,
    },
    {
      key: 'eugene-weekly',
      logo: `images/eugene-weekly.png`,
      alt: `Eugene Weekly logo`,
      title: t('news.dutchieBlossoms', 'dutchie Blossoms Thanks to Snoop Dogg’s Venture Capital Firm'),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / Eugene Weekly', date: 'September 9, 2019' }),
      url: `https://eugeneweekly.com/2018/09/24/dutchie-blossoms-thanks-to-snoop-doggs-venture-capital-firm/`,
    },
    {
      key: 'the-bulletin',
      logo: `images/the-bulletin.png`,
      alt: `The bulletin logo`,
      title: t('news.snoopFunds', "Snoop Dogg's Venture Fund Invests in Bend Startup"),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / The Bulletin', date: 'September 24, 2019' }),
      url: `https://www.bendbulletin.com/business/6547158-151/snoop-doggs-venture-fund-invests-in-bend-startup`,
    },
    {
      key: 'the-fresh-toast',
      logo: `images/the-fresh-toast.png`,
      alt: `fresh toast logo`,
      title: t('news.kdSnoopInvest', 'Kevin Durant And Snoop Dogg Invest In New Marijuana Delivery Service'),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / The Bulletin', date: 'September 28, 2019' }),
      url: `https://thefreshtoast.com/cannabis/kevin-durant-and-snoop-dogg-invest-in-new-marijuana-delivery-service`,
    },
    {
      key: 'tech-crunch',
      logo: `images/tech-crunch.png`,
      alt: `TechCrunch logo`,
      title: t(
        'news.theseBrothers',
        'These brothers just raised $15 million for their startup, Dutchie, a kind of Shopify for cannabis dispensaries'
      ),
      meta: t('news.dateCompany', { defaultValue: '{{date}} / TechCrunch', date: 'September 10, 2019' }),
      url: `https://techcrunch.com/2019/09/10/these-brothers-just-raised-15-million-for-their-startup-dutchie-a-kind-of-shopify-for-dispensaries/`,
    },
  ];
  const handleClick = () => {
    setVisiblePublications(publicationsMap.length);
  };
  const publicationsToDisplay = _.take(publicationsMap, visiblePublications);
  const hideButton = publicationsToDisplay.length === visiblePublications && visiblePublications !== 6;
  return (
    <Wrapper>
      <MetaTags variant='news' />
      <MarketingHeader />
      <BackgroundPoly />
      <Container>
        <HeaderContent center>
          <MobileOnly>
            <Header>{t('news.inTheNews', 'In the News')}</Header>
          </MobileOnly>
          <DesktopOnly>
            <Header>{t('news.dutchieNews', 'Dutchie in the news')}</Header>
          </DesktopOnly>
          <Label>{t('news.pressAndMedia', 'The top stories about dutchie in the press and media')}</Label>
        </HeaderContent>
        <SpotlightContainer>
          <MarketingSpotlight {...spotlightProps} />
        </SpotlightContainer>
      </Container>
      <NewsContainer>
        <PageHeader mobileCenter>
          <H2>{t('news.topStories', 'Top Stories')}</H2>
        </PageHeader>
        <NewsList container>
          {_.map(publicationsToDisplay, ({ alt, key, logo, title, meta, url }) => (
            <PublicationContainer item lg={4} md={4} sm={4} key={key} onClick={() => window.open(url, `_blank`)}>
              <Logo>
                <Imgix src={logo} htmlAttributes={{ alt }} />
              </Logo>
              <Content>
                <Title>{title}</Title>
                <Meta>{meta}</Meta>
              </Content>
            </PublicationContainer>
          ))}
        </NewsList>
        <ButtonContainer container justify='center'>
          {!hideButton && (
            <Button variant='outline' size='large' onClick={handleClick}>
              {t('common.loadMore', 'Load More')}
            </Button>
          )}
        </ButtonContainer>
      </NewsContainer>
      <Footer />
    </Wrapper>
  );
}

const BackgroundPoly = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 0;
  top: 0;

  &:before {
    content: '';
    width: 100%;
    height: 80%;
    position: absolute;
    top: -106px;
    left: 0;
    background: ${({ theme }) => theme.colors.primaryGrey};
    transform: skewY(8deg);
  }
`;

const ButtonContainer = styled(Grid)`
  margin-bottom: 150px;
`;

const Container = styled.div`
  position: relative;
  padding: 0 25px;
  margin: 0 auto;
  z-index: 20;
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 50px 25px 0;
  }
`;

const Content = styled.div`
  max-width: 325px;
`;

const HeaderContent = styled.div`
  margin-top: 55px; /* offset the nav height */
  margin-bottom: 50px;
  display: flex;
  place-items: center;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-bottom: 40px;
    text-align: center;
  }
`;

const PublicationContainer = styled(Grid)`
  max-width: 325px;
  margin-bottom: 80px !important;
  cursor: pointer;
  padding: 15px;
  &:hover {
    h3 {
      text-decoration: underline;
    }
  }
`;

const Logo = styled.div`
  max-width: 168px;
  display: flex;
  min-height: 30px;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: 0 auto;
    justify-content: center;
  }
  img {
    max-width: 160px;
    max-height: 30px;
    width: inherit;
    object-fit: contain;
    ${({ theme }) => theme.breakpoints.down('sm')} {
      width: 100%;
    }
  }
`;

const NewsContainer = styled(Container)`
  max-width: 1236px;
`;

const NewsList = styled(Grid)`
  width: 100%;
  flex-flow: row wrap;
  padding-top: 10px;
  margin: 0 auto;
  justify-content: flex-start;
  ${({ theme }) => theme.breakpoints.down('sm')} {
    text-align: center;
    justify-content: center;
  }
`;

const PageHeader = styled(HeaderContent)`
  margin-top: 115px;
`;

const SpotlightContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  p {
    &:hover {
      text-decoration: none;
    }
  }
`;
